import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SortDownArrow } from "../../../../assets/svg";
import Action from "../../../../components/common/Action";
import { DefaultPrimaryButton } from "../../../../components/micro/input";
import DebounceSearch from "../../../../components/common/DebounceSearch";
import RippleLoader from "../../../../components/loader/RippleLoader";
import CustomTable from "../../../../components/customTable/CustomTable";
import ConfirmationModel from "../../../../components/common/ConfirmationModel";
import ReactSelect from "react-select";
import { useDispatch, useSelector } from "react-redux";
import {
  DeleteTransfer,
  getEquipment,
  getOutlet,
} from "../../../../redux/companies/slice";
import { getProductList } from "../../../../redux/product/slice";
import AddEditTransfer from "../../modal/AddEditTransfer";

const Equipment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const storedData = sessionStorage.getItem("companyData");
  const companyData = storedData ? JSON.parse(storedData)?.company : null;
  const [searchValue, setSearchValue] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const [equipmentDelete, setEquipmentDelete] = useState(null);
  const [isPreferredOpen, setIsPreferredOpen] = useState(false);
  const [transferData, setTransferData] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [mode, setMode] = useState("");

  // Selector
  const { equipmentList, loading, productsList, outletList } = useSelector(
    (store) => ({
      equipmentList: store?.companiesData?.getEquipmentStatus?.data?.data,
      loading: store?.companiesData?.loading,
      productsList: store?.productsData?.getProductListStatus?.data?.data,
      outletList: store?.companiesData?.getOutletStatus?.data?.data,
    })
  );

  useEffect(() => {
    dispatch(
      getEquipment({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        equipment: selectedEquipment?.label || "",
        outlet: selectedOutlet?.label || "",
        company_id: companyData?.id || "",
      })
    );
    dispatch(
      getProductList({
        pageLimit: "all",
        search: "",
        sort_by: "name",
        sort_direction: "asc",
      })
    );
    dispatch(
      getOutlet({
        search: "",
        pageLimit: "all",
        company_id: companyData?.id || "",
        sort_by: '',
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      })
    );
  }, [
    searchValue,
    pageCount,
    pageLimit,
    selectedEquipment,
    selectedOutlet,
    sortBy,
    sortDirection,
  ]);

  const handleOutletChange = (selectedOption) => {
    setSelectedOutlet(selectedOption);
  };

  const handleEquipmentChange = (selectedOption) => {
    setSelectedEquipment(selectedOption);
  };

  const options = (outletList?.data || outletList)?.map((outlet) => ({
    value: outlet.id,
    label: outlet.name,
  }));

  const productOptions = (productsList?.data || productsList)?.map(
    (product) => ({
      value: product.id,
      label: product.name,
    })
  );

  const handleTransfer = ({ transfer, mode }) => {
    setTransferData(transfer);
    setMode(mode);
    setIsPreferredOpen(true);
  };

  const closeModal = () => {
    setIsPreferredOpen(false);
  };

  const handleEquipmentExportClick = () => {
    if (!equipmentList?.data || equipmentList?.data.length === 0) {
      alert("No data available to export!");
      return;
    }

    const header = [
      "Outlet Name",
      "Address",
      "Equipment",
      "Category",
      "Date of Purchase",
      "Warranty Status",
    ];

    const rows = equipmentList?.data.map((equipment) => [
      `"${equipment?.outlet?.name || "-"}"`,
      `"${equipment?.outlet?.address || "-"}"`,
      `"${equipment?.product?.name || "-"}"`,
      `"${equipment?.equipment_count || "-"}"`,
      `"${equipment?.product?.category?.name || "-"}"`,
      `"${equipment?.purchase_date || "-"}"`,
      `"${equipment?.status || "-"}"`,
    ]);

    let csvContent = header.join(",") + "\n";
    rows.forEach((row) => {
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "equipments_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Delete handler
  const deleteEquipmentId = async () => {
    setIsLoading(true);
    try {
      const res = await dispatch(DeleteTransfer(equipmentDelete));
      if (res.type === "DeleteTransfer/fulfilled") {
        await dispatch(
          getEquipment({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
            equipment: selectedEquipment?.label || "",
            outlet: selectedOutlet?.label || "",
            company_id: companyData?.id || "",
          })
        );
      }
    } catch (error) {
      console.error("Error deleting remark:", error);
    } finally {
      setConfirmDelete(false);
      setIsLoading(false);
    }
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        No.
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center cursor-pointer"
          onClick={() => {
            setSortBy("outlet");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Outlet Name</span>
          <SortDownArrow
            className={`ml-1 transition-transform ${
              sortDirection === "asc" && sortBy === "outlet" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center cursor-pointer"
          onClick={() => {
            setSortBy("address");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Address</span>
          <SortDownArrow
            className={`ml-1 transition-transform ${
              sortDirection === "asc" && sortBy === "address"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center cursor-pointer"
          onClick={() => {
            setSortBy("equipment");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Equipment</span>
          <SortDownArrow
            className={`ml-1 transition-transform ${
              sortDirection === "asc" && sortBy === "equipment"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center cursor-pointer"
          onClick={() => {
            setSortBy("category");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Category</span>
          <SortDownArrow
            className={`ml-1 transition-transform ${
              sortDirection === "asc" && sortBy === "category"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Date of Purchase
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center cursor-pointer"
          onClick={() => {
            setSortBy("warranty_status");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span> Warranty Status</span>
          <SortDownArrow
            className={`ml-1 transition-transform ${
              sortDirection === "asc" && sortBy === "warranty_status"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Actions
      </th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(equipmentList?.data || []).map((item, index) => (
        <tr
          key={item.id}
          className="font-Proxima shadow-sm text-mgrey hover:bg-lightprimaryorange "
        >
          <td className="px-5 py-3 truncate">{index + 1}</td>
          <td className="px-5 py-3 truncate">{item?.outlet?.name || "-"}</td>
          <td className="px-5 py-3 truncate">{item?.outlet?.address || "-"}</td>
          <td className="px-5 py-3 truncate">{item?.product?.name || "-"}</td>
          <td className="px-5 py-3 truncate">
            {item?.product?.category?.name || "-"}
          </td>
          <td className="px-5 py-3 truncate">{item?.purchase_date}</td>
          <td className={`px-5 py-3 truncate w-12`}>
            <div
              className={`rounded-xl bg-white py-0.5 px-2 border text-sm text-center ${
                item.status === "Invalid"
                  ? "text-red border-red"
                  : item.status === "valid"
                  ? "text-green-500 border-green-500"
                  : item.status === "Not Applicable"
                  ? "text-gray-500 border-gray-500"
                  : "text-gray-dark border-gray-dark"
              }`}
            >
              {item?.status}
            </div>
          </td>
          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() =>
                      navigate("/create-edit-equipment", {
                        state: { equipment: item, mode: "edit" },
                      })
                    }
                  >
                    View/Edit
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      handleTransfer({ transfer: item, mode: "Equipment" });
                    }}
                  >
                    Transfer Equipment
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setConfirmDelete(true);
                      setEquipmentDelete(item?.id);
                    }}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <>
      <div className="space-y-5  w-full bg-white shadow rounded-lg p-4">
        {/* Search and Export Section */}
        <div className="flex items-center space-x-3">
          <ReactSelect
            className={"w-full max-w-52 text-black p-0 font-Proxima"}
            id="outlet"
            placeholder="Outlet"
            options={options}
            isClearable={true}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            value={selectedOutlet}
            onChange={handleOutletChange}
            styles={{
              control: (base) => ({
                ...base,
                border: `1px solid #0000003B`,
                // borderColor: selectedCategories ? "#F15B29" : "#0000003B",
                borderRadius: "0.3rem",
                boxShadow: "none",
                "&:hover": {
                  border: "1px solid #0000003B",
                },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#F15B29" : "white",
                "&:active": {
                  backgroundColor: state.isSelected
                    ? "#F15B29"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }),
            }}
          />
          <ReactSelect
            className={"w-full max-w-52 text-black p-0 font-Proxima"}
            id="equipment"
            placeholder="Equipment"
            options={productOptions}
            isClearable={true}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            value={selectedEquipment}
            onChange={handleEquipmentChange}
            styles={{
              control: (base) => ({
                ...base,
                border: `1px solid #0000003B`,
                borderRadius: "0.3rem",
                boxShadow: "none",
                "&:hover": {
                  border: "1px solid #0000003B",
                },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#F15B29" : "white",
                "&:active": {
                  backgroundColor: state.isSelected
                    ? "#F15B29"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }),
            }}
          />
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <DefaultPrimaryButton
            value="EXPORT"
            onClick={handleEquipmentExportClick}
          />
        </div>

        {/* Loading or Table */}
        {loading ? (
          <RippleLoader />
        ) : (
          <div>
            <CustomTable
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              overflowVisibleLg={true}
              data={equipmentList}
              showPagination
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              total={equipmentList?.total}
              current={pageCount}
              lastPage={Math.ceil(equipmentList?.total / pageLimit)}
              paginationData={(crPage) => setPageCount(crPage)}
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <ConfirmationModel
          confirmText="Delete"
          onClose={() => setConfirmDelete(false)}
          onConfirm={deleteEquipmentId}
          isLoading={isLoading}
        />
      )}
      {isPreferredOpen && (
        <div>
          <AddEditTransfer
            onClose={closeModal}
            companyId={companyData?.id}
            transferData={transferData}
            mode={mode}
          />
        </div>
      )}
    </>
  );
};

export default Equipment;
